import { useState } from "react";

import { ChevronRightIcon } from "lucide-react";

import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";

import { cn } from "@/lib/utils";

import { NavAnchor } from "./nav-anchor";
import { ACTIVE_MARKER } from "./nav-styles";
import { type NavNode, isBranch } from "./nav-tree";

interface AccordionProps {
  node: NavNode;
  /** Havola bosilganda — telefondagi sheet shu bilan yopiladi. */
  onNavigate?: () => void;
}

/**
 * Joriy sahifa guruh ichida bo'lsa, guruh o'zi ochiq turadi — qaysi sahifada
 * turganini ko'rish uchun menyuni qo'lda ochish kerak bo'lmasin.
 */
function useExpanded(active: boolean) {
  const [open, setOpen] = useState(active);
  return [open || active, () => setOpen(previous => !previous)] as const;
}

/**
 * Ochiq paneldagi (va telefondagi sheet'dagi) bolali band: bosilganda ostida
 * ro'yxat ochiladi. Yig'ilgan panelda ro'yxatga joy yo'q — u yerda
 * `NavFlyout` ishlaydi.
 *
 * Ro'yxat `<li>` ning ICHIDA: ilgari u qo'shni bo'lib turardi va `<ul>` ichida
 * to'g'ridan-to'g'ri `<ul>` hosil bo'lardi — yaroqsiz HTML.
 */
export function NavAccordion({ node, onNavigate }: AccordionProps) {
  const [expanded, toggle] = useExpanded(node.active);

  return (
    <SidebarMenuItem data-active={node.active} className={ACTIVE_MARKER}>
      <SidebarMenuButton isActive={node.active} aria-expanded={expanded} onClick={toggle}>
        {node.icon && <node.icon />}
        <span>{node.label}</span>
        <ChevronRightIcon className={cn("ml-auto transition-transform", expanded && "rotate-90")} />
      </SidebarMenuButton>

      {expanded && (
        <SidebarMenuSub className="mt-1">
          <AccordionEntries nodes={node.children ?? []} onNavigate={onNavigate} />
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

function AccordionEntries({
  nodes,
  onNavigate,
}: {
  nodes: readonly NavNode[];
  onNavigate?: () => void;
}) {
  return nodes.map(node =>
    isBranch(node) ? (
      <AccordionBranch key={node.key} node={node} onNavigate={onNavigate} />
    ) : (
      <SidebarMenuSubItem key={node.key}>
        <SidebarMenuSubButton asChild isActive={node.active}>
          <NavAnchor
            href={node.href}
            aria-current={node.active ? "page" : undefined}
            onClick={onNavigate}
          >
            {node.icon && <node.icon />}
            <span>{node.label}</span>
          </NavAnchor>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    ),
  );
}

/** Ichki qavatdagi guruh — yuqoridagi bilan bir xil qoida, bir pog'ona kichik qator. */
function AccordionBranch({ node, onNavigate }: AccordionProps) {
  const [expanded, toggle] = useExpanded(node.active);

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={node.active}>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={toggle}
          className="w-full text-left"
        >
          {node.icon && <node.icon />}
          <span>{node.label}</span>
          <ChevronRightIcon
            className={cn("ml-auto transition-transform", expanded && "rotate-90")}
          />
        </button>
      </SidebarMenuSubButton>

      {expanded && (
        <SidebarMenuSub className="mt-1">
          <AccordionEntries nodes={node.children ?? []} onNavigate={onNavigate} />
        </SidebarMenuSub>
      )}
    </SidebarMenuSubItem>
  );
}
